import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useParams} from 'react-router-dom';
import dayjs, {Dayjs} from 'dayjs';
import {
  Box,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {apiPost} from '../../../../axiosConfig';
import DialogApiRequestStatus from '../../../../components/dialogs/DialogApiRequestStatus';
import {useRqGetMessagesForScriptVerification} from '../../../../react_query/messages/task/ListMessgesForScriptVerification';
import DialogNotes from '../../../../components/dialogs/DialogNotes';
import BackCloseButton from '../../../../components/styles/BackCloseButton';
import {IMediaTrack} from '../../../../dto/MediaTrack.dto';
import MusicPreviewBtn from '../../../../components/music/MusicPreviewBtn';
import ExistingCampaignChooserBtn from '../../../sponsor/ExistingCampaignChooserBtn';
import {SponsorRequestDetailsCard} from '../../../sponsor/SponsorshipRequest';
import {ConditionalRender, UserCanAny} from '../../../../components/UserCan';
import {useRqUsersByTaskSlug} from '../../../../react_query/tasks/UsersByTask';
import UploadButton from '../../../../components/styles/UploadButton';
import {SelectAiVoicePersons} from '../../../sponsor/SelectAiVoicePersons';
import {FilePreview} from './AudioApproval';
import {useTaskContext} from '../../../task/TaskContext';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {BackDropCmpt} from '../../../../components/layouts/AuthUsers';
import {GlobalBtnWithConfirmationDialog} from '../../../../components/dialogs/MessageOk';
import sponsorConfig from '../../../../config/messageConfig';
import {sponsorCommercialAppendTrackInfo} from './sponsorCommercialAppendTrackInfo';
import {broadCastCloseTab} from '../../../../react_query/tasks/ListTasks';
import {useRqGetSponsorById} from '../../../../react_query/sponsor_commercial/SponsorCommercialQuery';
const MessageApproval = () => {
  const task = useParams();
  const taskId = Number(task.task_id);
  const {data, isFetching} = useRqGetMessagesForScriptVerification(taskId);
  const [apiState, setApiState] = useState({status: '', error: null});
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [reassignNote, SetReassignNote] = useState('');
  const [type, setType] = useState('');
  const {companyId} = useTaskContext();
  const isAwaitingSponsorDetails =
    data?.task_slug === 'sponsor-provides-detail-review';
  const {data: sponsorRequest} = useRqGetSponsorById(
    companyId,
    isAwaitingSponsorDetails && companyId > 0
      ? Number(data?.requestable_id)
      : 0,
  );
  const [selectedTrack, setSelectedTrack] = useState<
    (IMediaTrack & {created_at?: string}) | null
  >(null);
  const [copywriterAssignee, setCopywriterAssignee] = useState(0);
  const [aiVoiceAudioFile, setAiVoiceAudioFile] = useState<File | null>(null);
  const [selectAiVoicePerson, setSelectedAiVoicePerson] = useState<number>(0);
  const [file, setFile] = useState<File | null>();
  const [fileError, setFileError] = useState('');
  const [isLegacyVoice, setIsLegacyVoice] = useState(false);
  const [stepperLoadedOn, setStepperLoadedOn] = useState<Dayjs | null>(null);
  useEffect(() => {
    setStepperLoadedOn(dayjs());
  }, []);

  const isAiVoiceRequired = useMemo(() => {
    return [
      'sponsor-content-review-to-copywriter',
      'no-details-sent-to-copywriter',
    ].includes(data?.task_slug || '');
  }, [data?.task_slug]);

  useEffect(() => {
    if (data?.requestable?.messages?.length) {
      const initialMessage = data?.requestable?.messages[0]?.message ?? '';
      const initialCopywriterContent =
        data?.requestable?.messages[0]?.copy_writer_content ?? '';

      if (
        [
          'sponsor-content-review-to-copywriter',
          'sent-to-copywriter',
          'no-details-sent-to-copywriter',
          'sponsor-content-review',
        ].includes(data?.task_slug)
      ) {
        setMessage(initialCopywriterContent);
      } else {
        setMessage(initialMessage);
      }
    }
  }, [data]);

  const postAPI = (url: string, formData: FormData) => {
    return apiPost(url, formData)
      .then(() => {
        setApiState({
          status: 'success',
          error: null,
        });
        broadCastCloseTab();
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  };

  const handleApproveStatus = (type: string) => {
    setType(type);
    const formData = new FormData();
    formData.append('type', type);
    formData.append('message', message);

    if (isAiVoiceRequired) {
      if (selectAiVoicePerson) {
        formData.append('ai_voice', selectAiVoicePerson.toString());
      }
      formData.append('ai_voice_file', aiVoiceAudioFile as File);
    }
    sponsorCommercialAppendTrackInfo(formData, selectedTrack, stepperLoadedOn);

    if (file) {
      formData.append('file', file);
    }
    postAPI(`/v1/task/${taskId}/audio/verification`, formData);
  };

  const handleVerificationStatus = (type: string) => {
    setApiState({status: 'pending', error: null});
    setType(type);
    const fd = new FormData();
    fd.append('type', type);
    fd.append('message', type === 'reassign' ? reassignNote : message);
    if (selectedTrack?.id && Boolean(selectedTrack?.id))
      fd.append('mediaTrackId', selectedTrack?.id.toString());
    if (Boolean(copywriterAssignee))
      fd.append('copy_writer_assignee', copywriterAssignee.toString());

    if (isAiVoiceRequired) {
      if (selectAiVoicePerson) {
        fd.append('ai_voice_person', selectAiVoicePerson.toString());
      }
      fd.append('ai_voice_audio_file', aiVoiceAudioFile as File);
    }

    apiPost(`/v1/task/${taskId}/message/verification`, fd)
      .then(() => {
        setApiState({
          status: 'success',
          error: null,
        });
        broadCastCloseTab();
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  };
  const handleClose = () => {
    setOpen(false);
    setMessage('');
  };
  const handleSubmit = () => {
    setApiState({status: 'pending', error: null});
    const payload = {
      message,
    };
    apiPost(`/v1/task/${taskId}/message/resubmit`, payload)
      .then(() => {
        setApiState({
          status: 'success',
          error: null,
        });
        broadCastCloseTab();
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  };
  const handleMessage = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  }, []);
  const handleReassignMessage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      SetReassignNote(event.target.value);
    },
    [],
  );
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null});
  }, []);
  const handleCopywriterSubmit = () => {
    const fd = new FormData();
    if (isAiVoiceRequired) {
      if (selectAiVoicePerson) {
        fd.append('ai_voice', selectAiVoicePerson.toString());
      }
      fd.append('ai_voice_file', aiVoiceAudioFile as File);
    }
    fd.append('message', message);

    apiPost(`/v1/task/${taskId}/message/copywriter`, fd)
      .then(() => {
        setApiState({
          status: 'success',
          error: null,
        });
        broadCastCloseTab();
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  };
  const handleCopyWriterVerificationStatus = (type: string) => {
    setApiState({status: 'pending', error: null});
    setType(type);
    const payload = {
      type,
      message,
    };
    if (type === 'reassign') {
      payload.message = reassignNote;
    } else {
      payload.message = message;
    }
    apiPost(`/v1/task/${taskId}/message/copywriter-verification`, payload)
      .then(() => {
        setApiState({
          status: 'success',
          error: null,
        });
        broadCastCloseTab();
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  };

  const handleRetry = () => {
    if (
      [
        'verify-partner-provided-details',
        'verify-sponsor-provided-details',
      ].includes(String(data?.task_slug))
    ) {
      handleVerificationStatus(type);
    }
    if (data?.task_slug === 'partner-provides-details') {
      handleSubmit();
    }
    if (data?.task_slug === 'sponsor-content-review') {
      handleCopyWriterVerificationStatus(type);
    }
  };

  const onClose = () => {
    setApiState({status: 'pending', error: null});
    broadCastCloseTab();
  };

  const isSubmitBtnDisabled = useMemo(
    () =>
      !selectedTrack &&
      !(isLegacyVoice && aiVoiceAudioFile) &&
      (isAiVoiceRequired
        ? !aiVoiceAudioFile || !selectAiVoicePerson
        : message === ''),
    [
      selectedTrack,
      isAiVoiceRequired,
      isLegacyVoice,
      message,
      aiVoiceAudioFile,
      selectAiVoicePerson,
    ],
  );

  const isDisabled = useCallback(() => {
    if (
      data?.task_slug &&
      [
        'verify-partner-provided-details',
        'verify-sponsor-provided-details',
        'sponsor-no-details-provided',
      ].includes(data?.task_slug)
    ) {
      return !copywriterAssignee;
    }
    return false;
  }, [copywriterAssignee, data?.task_slug]);

  const handleDeleteOfExistSong = useCallback(() => {
    setSelectedTrack(null);
  }, []);

  const handleAiVoiceUpload = useCallback((file: string | File | undefined) => {
    if (file && typeof file !== 'string') {
      setAiVoiceAudioFile(file);
      setFile(file);
      setSelectedTrack(null);
      setFileError('');
    }
  }, []);

  if (isFetching) {
    return <BackDropCmpt loader={true} />;
  }

  return (
    <>
      <Box className='m-2 blue-variant17 p-2'>
        <BackCloseButton title='' taskId={taskId} />
        <List>
          <ListItem>
            {data?.requestable_id ? (
              <UserCanAny scope='is-admin,sponsor:view,partner'>
                <SponsorRequestDetailsCard
                  sponsorRequestId={data?.requestable_id}
                />
              </UserCanAny>
            ) : null}
          </ListItem>
          <ListItem>
            <Typography
              variant='body1'
              component='div'
              className='w-248 text-bold'>
              Submitted At
            </Typography>
            <Typography variant='subtitle1' component='div'>
              :{' '}
              {dayjs(data?.requestable.created_at).format(
                'MM/DD/YYYY hh:mm:ss A',
              )}
            </Typography>
          </ListItem>
          <ListItem>
            <Typography
              variant='body1'
              component='div'
              className='w-248 text-bold'>
              Last Updated
            </Typography>
            <Typography variant='subtitle1' component='div'>
              :{' '}
              {dayjs(data?.requestable.updated_at).format(
                'MM/DD/YYYY hh:mm:ss A',
              )}
            </Typography>
          </ListItem>
          <ConditionalRender
            condition={
              Boolean(data?.requestable?.ai_voice) &&
              ['sponsor-content-review'].includes(data?.task_slug ?? '')
            }>
            <ListItem>
              <Typography
                variant='body1'
                component='div'
                className='w-248 text-bold'>
                AI voice Used
              </Typography>
              <Typography variant='subtitle1' component='div'>
                : {data?.requestable?.ai_voice}{' '}
                <ConditionalRender
                  condition={Boolean(data?.requestable?.ai_voice_file)}>
                  <>
                    <MusicPreviewBtn
                      id={'ai-voice-preview'}
                      url={data?.requestable.ai_voice_file?.file_url ?? ''}
                      track_name={
                        data?.requestable.ai_voice_file?.file_name ?? ''
                      }
                      track_artist={`${data?.requestable?.ai_voice}_${data?.requestable_id}.mp3`}
                      isDownloadable
                      downloadFileName={`${data?.requestable?.ai_voice}_${data?.requestable_id}.mp3`}
                    />
                  </>
                </ConditionalRender>
              </Typography>
            </ListItem>
          </ConditionalRender>
          {data?.requestable.proposed_message.map(value => {
            if (!Boolean(value.is_completed)) {
              return (
                <Box key={value.id} mx={2}>
                  <Typography variant='body1'>
                    Revised Notes from Sponsor
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={value.proposed_message}
                    disabled
                  />
                </Box>
              );
            } else {
              return null;
            }
          })}

          {data?.requestable.message?.rejected_note &&
          data?.task_slug === 'sponsor-content-review-to-copywriter' ? (
            <Box mx={2}>
              <Typography variant='body1' style={{width: '20%'}}>
                Revised Notes
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={data?.requestable.message?.rejected_note}
                disabled
              />
            </Box>
          ) : null}
        </List>
        {data?.requestable_type === 'sponsor-commercial' &&
        [
          'verify-partner-provided-details',
          'verify-sponsor-provided-details',
        ].includes(String(data?.task_slug)) ? (
          <Box className='ml-3'>
            <ExistingCampaignChooserBtn
              tracks={[]}
              onChange={(tracks: IMediaTrack[]) => {
                if (tracks.length > 0) {
                  setSelectedTrack(tracks[0] as IMediaTrack);
                }
              }}
              label='Choose Sponsor Commercial'
              type='sponsor-commercial'
              max={1}
              companyId={companyId}
            />
          </Box>
        ) : null}
        {data?.task_slug &&
        [
          'verify-partner-provided-details',
          'verify-sponsor-provided-details',
          'sponsor-no-details-provided',
        ].includes(data?.task_slug) ? (
          <Stack className='m-3' direction={'row'} spacing={2}>
            <SelectAssigneeForTaskCmpt
              taskSlug='sent-to-copywriter'
              companyId={companyId}
              label='Copy Writer'
              assignee={copywriterAssignee}
              setAssignee={setCopywriterAssignee}
            />
          </Stack>
        ) : null}
        {data?.task_slug &&
        data?.task_slug !== 'sponsor-provides-detail-review' ? (
          <Box mx={2}>
            <Typography variant='h6' aria-level={2}>
              Original Message
            </Typography>
            {data?.requestable.messages.map(value => {
              const messageTitle = value?.name || value?.requestable?.name;
              return (
                <div key={value.id}>
                  {messageTitle && (
                    <Typography variant='caption' className='mb-1 block'>
                      <b>Name: </b>
                      {messageTitle}
                    </Typography>
                  )}
                  {data.task_slug !== 'no-details-sent-to-copywriter' ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={
                        !['sent-to-copywriter'].includes(data?.task_slug)
                          ? value.original_message
                          : value.message
                      }
                      id='message-text-field'
                      disabled={
                        ![
                          'partner-provides-details',
                          'verify-partner-provided-details',
                          'verify-sponsor-provided-details',
                        ].includes(data?.task_slug)
                      }
                      onChange={handleMessage}
                      defaultValue={value.message}
                    />
                  ) : null}
                  <ConditionalRender
                    condition={[
                      'sponsor-content-review-to-copywriter',
                      'sent-to-copywriter',
                      'no-details-sent-to-copywriter',
                      'sponsor-content-review',
                    ].includes(data?.task_slug)}>
                    <React.Fragment>
                      <Typography variant='h6'>Copywriter Content</Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={value.copy_writer_content}
                        id='copywriter-message-text-field'
                        disabled={data?.task_slug === 'sponsor-content-review'}
                        defaultValue={value.copy_writer_content}
                        onChange={handleMessage}
                      />
                    </React.Fragment>
                  </ConditionalRender>
                  <ConditionalRender condition={isAiVoiceRequired}>
                    <React.Fragment>
                      <Typography variant='h6'>Attach File</Typography>
                      {fileError && (
                        <Alert
                          severity='error'
                          sx={{my: 1, width: 'max-content'}}
                          onClose={() => setFileError('')}>
                          {fileError}
                        </Alert>
                      )}
                      <Stack
                        direction='row'
                        gap={1}
                        className='w-full flex-column'>
                        <Box className='flex items-center w-full gap-2'>
                          <Box className='flex flex-column gap-1'>
                            <UploadButton
                              handleChange={handleAiVoiceUpload}
                              setFileError={setFileError}
                            />
                          </Box>
                          <ExistingCampaignChooserBtn
                            tracks={[]}
                            onChange={(tracks: IMediaTrack[]) => {
                              if (tracks.length > 0) {
                                setSelectedTrack(tracks[0] as IMediaTrack);
                                setAiVoiceAudioFile(null);
                                setFile(null);
                                setFileError('');
                              }
                            }}
                            label='Choose Sponsor Commercial'
                            fileUploadLabel='Attach Processed File'
                            type='sponsor-commercial'
                            max={1}
                            companyId={companyId}
                          />
                          <Stack alignItems='center'>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isLegacyVoice}
                                  onChange={e => {
                                    setIsLegacyVoice(e.target.checked);
                                    if (e.target.checked) {
                                      setSelectedAiVoicePerson(0);
                                    }
                                  }}
                                />
                              }
                              label='Legacy Voice'
                            />
                            {data.company?.id && (
                              <SelectAiVoicePersons
                                setSelectedAiVoicePerson={
                                  setSelectedAiVoicePerson
                                }
                                selectedAiVoicePerson={selectAiVoicePerson}
                                companyId={data.company.id}
                                disabled={isLegacyVoice}
                              />
                            )}
                          </Stack>
                        </Box>
                        <Box className='w-full'>
                          <Box sx={{width: 'fit-content'}}>
                            <ConditionalRender
                              condition={Boolean(aiVoiceAudioFile)}>
                              <FilePreview
                                file={aiVoiceAudioFile as File}
                                setFile={setAiVoiceAudioFile}
                              />
                            </ConditionalRender>
                          </Box>
                        </Box>
                      </Stack>
                    </React.Fragment>
                  </ConditionalRender>
                </div>
              );
            })}
          </Box>
        ) : null}
        {selectedTrack ? (
          <Stack
            className='p-2 radius-5 mt-2 flex-row items-center w-full gap-2'
            sx={{
              border: '1px solid var(--gray-opacity-variant2)',
              width: 'fit-content',
            }}>
            <Typography>{selectedTrack?.track_name}</Typography>
            <MusicPreviewBtn
              id={selectedTrack.id}
              url={selectedTrack.url}
              key={selectedTrack.url}
              track_name={selectedTrack?.track_name}
              track_artist={selectedTrack?.track_artist}
            />
            <IconButton onClick={handleDeleteOfExistSong}>
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ) : null}
        <Stack
          spacing={1}
          direction='row'
          sx={{alignItems: 'center', justifyContent: 'center', m: 2}}>
          {isAwaitingSponsorDetails &&
          sponsorRequest?.sponsor_notification?.token ? (
            <Button
              variant='contained'
              component='a'
              href={`/sponsor/link?token=${encodeURIComponent(
                sponsorRequest.sponsor_notification.token,
              )}`}>
              Submit Ad Information
            </Button>
          ) : null}
          {data?.task_slug === 'sponsor-no-details-provided' ? (
            <Button
              variant='outlined'
              disabled={isDisabled()}
              onClick={() => handleVerificationStatus('approved')}>
              To Copywriter
            </Button>
          ) : null}
          {[
            'verify-partner-provided-details',
            'sponsor-content-review',
            'verify-sponsor-provided-details',
          ].includes(String(data?.task_slug)) && (
            <>
              <Button
                variant='outlined'
                disabled={isDisabled()}
                onClick={() =>
                  [
                    'verify-partner-provided-details',
                    'verify-sponsor-provided-details',
                  ].includes(String(data?.task_slug))
                    ? handleVerificationStatus('approved')
                    : handleCopyWriterVerificationStatus('approved')
                }>
                {selectedTrack
                  ? 'Upload And Submit'
                  : data && data?.task_slug === 'sponsor-content-review'
                  ? ' Approve '
                  : ' To Copywriter'}
              </Button>
              {([
                'verify-partner-provided-details',
                'verify-sponsor-provided-details',
              ].includes(String(data?.task_slug)) &&
                (!data?.requestable.proposed_message ||
                  data?.requestable.proposed_message.length === 0)) ||
              'sponsor-content-review' === data?.task_slug ? (
                <Button variant='outlined' onClick={() => setOpen(true)}>
                  Revise Info
                </Button>
              ) : null}
            </>
          )}
          {'sent-to-copywriter' === data?.task_slug ? (
            <GlobalBtnWithConfirmationDialog
              dialog={{
                dialogContent: <>{sponsorConfig.overrideSponsorConfirmTask}</>,
                dialogTitle: 'Override confirmation',
              }}
              buttonText='Override'
              buttonProps={{disabled: !message}}
              submitButton={{
                text: 'Yes',
                action: () =>
                  handleCopyWriterVerificationStatus('override-approval'),
              }}
              cancelButton={{
                text: 'No',
              }}
            />
          ) : null}
          {[
            'partner-provides-details',
            'sponsor-content-review-to-copywriter',
            'sent-to-copywriter',
            'no-details-sent-to-copywriter',
          ].includes(String(data?.task_slug)) && (
            <Button
              variant='contained'
              onClick={
                data?.task_slug === 'partner-provides-details'
                  ? handleSubmit
                  : [
                      'sponsor-content-review-to-copywriter',
                      'no-details-sent-to-copywriter',
                    ].includes(data?.task_slug ?? '')
                  ? () =>
                      handleApproveStatus(
                        data?.task_slug === 'sponsor-file-review'
                          ? 'partner-approval'
                          : data?.requestable?.message?.is_manual_upload === 1
                          ? 'file-approval'
                          : 'approved',
                      )
                  : handleCopywriterSubmit
              }
              disabled={isSubmitBtnDisabled}>
              Submit
            </Button>
          )}
        </Stack>
      </Box>
      <DialogNotes
        open={open}
        note={reassignNote}
        handleChange={handleReassignMessage}
        handleSubmit={
          [
            'verify-partner-provided-details',
            'verify-sponsor-provided-details',
          ].includes(String(data?.task_slug))
            ? handleVerificationStatus
            : handleCopyWriterVerificationStatus
        }
        handleClose={handleClose}
      />
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleRetry}
          onEdit={handleEdit}
          onClose={onClose}
        />
      )}
    </>
  );
};
export default MessageApproval;

export const SelectAssigneeForTaskCmpt = ({
  companyId,
  taskSlug,
  label,
  assignee,
  setAssignee,
}: {
  companyId: number;
  taskSlug: string;
  label: string;
  assignee: number;
  setAssignee: (val: number) => void;
}) => {
  const {data, isFetching} = useRqUsersByTaskSlug(
    taskSlug,
    companyId,
    Boolean(companyId),
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      setAssignee(Number(event.target.value));
    },
    [setAssignee],
  );

  return (
    <FormControl className='w-300 my-3'>
      <InputLabel id={`task-assign-label`}>{`${label} Assignee`}</InputLabel>
      <Select
        labelId={`task-assign-label`}
        value={assignee.toString()}
        label={`${label} Assignee`}
        onChange={handleChange}>
        <MenuItem disabled>{`Choose ${label} Assignee`}</MenuItem>
        {!isFetching &&
          data &&
          data.map(item => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
